/**
 * Gender codes for member records Infinity stores (tenant member docs, payroll files).
 *
 * VBA is the exception: its `Sex` column takes `M | F | X`, and the VBA write paths
 * collapse anything that isn't M/F to `X` at that boundary.
 */
export const GENDER_CODES = ['M', 'F', 'O'] as const;
export type GenderCode = (typeof GENDER_CODES)[number];

export const GENDER_LABELS: Record<GenderCode, string> = {F: 'Female', M: 'Male', O: 'Other'};

export function formatGenderLabel(value: string | null | undefined): string | null {
  if (value == null) return null;
  return GENDER_LABELS[value as GenderCode] ?? value;
}

// assumes input has all non-alphanumeric characters removed and is lowercase
export const GENDER_VARIANT_MAP: Record<string, GenderCode> = {
  boy: 'M',
  chosenottoanswer: 'O',
  f: 'F',
  female: 'F',
  girl: 'F',
  m: 'M',
  male: 'M',
  man: 'M',
  nb: 'O',
  nonbinary: 'O',
  notspecified: 'O',
  o: 'O',
  other: 'O',
  prefernottoanswer: 'O',
  prefernottoidentify: 'O',
  u: 'O',
  undisclosed: 'O',
  unknown: 'O',
  unspecified: 'O',
  woman: 'F',
  x: 'O',
};

/** Maps free-text gender ("Male", "f", "Non-binary") to a code; null when blank or unrecognized. */
export function normalizeGender(value: string | null | undefined): GenderCode | null {
  const key = (value ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
  return GENDER_VARIANT_MAP[key] ?? null;
}

/** The `M | F | X` VBA's `Sex` column takes: recognized M/F spellings keep it, all else is X. */
export function toVbaSexCode(value: string | null | undefined): 'M' | 'F' | 'X' {
  const code = normalizeGender(value);
  return code === 'M' || code === 'F' ? code : 'X';
}
